import { Search, X } from 'lucide-react';
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { HELP_CATEGORIES, HELP_CONTENT, type HelpNodeContent } from '../data/help-content';
import { HelpArticle } from './HelpArticle';

interface HelpMenuProps {
	isOpen: boolean;
	onClose: () => void;
	initialNodeId?: string;
	embedded?: boolean;
	popup?: boolean; // New prop for popup mode when embedded
}

export function HelpMenu({ isOpen, onClose, initialNodeId, embedded = false, popup = false }: HelpMenuProps) {
	const [searchTerm, setSearchTerm] = useState('');
	const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
	const [selectedNode, setSelectedNode] = useState<HelpNodeContent | null>(
		initialNodeId ? HELP_CONTENT.find(node => node.id === initialNodeId) || null : null
	);

	// Filter content based on search and category
	const filteredContent = HELP_CONTENT.filter(node => {
		const matchesSearch =
			searchTerm === '' ||
			node.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
			node.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
			node.whenToUse.some(use => use.toLowerCase().includes(searchTerm.toLowerCase()));

		const matchesCategory = selectedCategory === null || node.category === selectedCategory;

		return matchesSearch && matchesCategory;
	});

	// Group content by category
	const groupedContent = HELP_CATEGORIES.map(category => ({
		...category,
		nodes: filteredContent.filter(node => node.category === category.id),
	}));

	const handleNodeSelect = (node: HelpNodeContent) => {
		setSelectedNode(node);
	};

	const handleBack = () => {
		setSelectedNode(null);
	};

	if (!isOpen) return null;

	// If embedded and popup mode, render as popup overlay using Portal
	if (embedded && popup) {
		const popupContent = (
			<div 
				className="fixed inset-0 z-[999999] flex bg-black/50 pointer-events-auto" 
				style={{ zIndex: 999999 }}
				onClick={(e) => e.stopPropagation()}
				onMouseDown={(e) => e.stopPropagation()}
				onMouseUp={(e) => e.stopPropagation()}
			>
				{/* Overlay */}
				<div className="absolute inset-0" onClick={onClose} />

				{/* Help Menu Popup - Using exact same styling as original */}
				<div 
					className="relative flex w-full max-w-6xl mx-auto my-8 bg-white dark:bg-gray-800 rounded-lg shadow-xl pointer-events-auto"
					onClick={(e) => e.stopPropagation()}
					onMouseDown={(e) => e.stopPropagation()}
					onMouseUp={(e) => e.stopPropagation()}
				>
					{/* Sidebar */}
					<div 
						className="w-80 flex flex-col border-r border-gray-200 dark:border-gray-700"
						onClick={(e) => e.stopPropagation()}
					>
						{/* Header with close button */}
						<div className="p-4 border-b border-gray-200 dark:border-gray-700">
							<div className="flex items-center justify-between mb-4">
								<h2 className="text-xl font-bold text-gray-900 dark:text-white">
									Help & Documentation
								</h2>
								<button
									onClick={onClose}
									className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
								>
									<X className="w-5 h-5" />
								</button>
							</div>
							
							{/* Search */}
							<div className="relative">
								<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 w-4 h-4" />
								<input
									type="text"
									placeholder="Search nodes..."
									value={searchTerm}
									onChange={e => setSearchTerm(e.target.value)}
									className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
								/>
							</div>
						</div>

						{/* Category Filter */}
						<div className="p-4 border-b border-gray-200 dark:border-gray-700">
							<div className="flex flex-wrap gap-2">
								<button
									onClick={() => setSelectedCategory(null)}
									className={`px-3 py-1 text-sm rounded-md transition-colors ${
										selectedCategory === null
											? 'bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900'
											: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
									}`}
								>
									All
								</button>
								{HELP_CATEGORIES.map(category => (
									<button
										key={category.id}
										onClick={() => setSelectedCategory(category.id)}
										className={`px-3 py-1 text-sm rounded-md transition-colors ${
											selectedCategory === category.id
												? `bg-${category.color}-600 dark:bg-${category.color}-500 text-white`
												: `bg-${category.color}-100 dark:bg-${category.color}-900 text-${category.color}-700 dark:text-${category.color}-300 hover:bg-${category.color}-200 dark:hover:bg-${category.color}-800`
										}`}
									>
										{category.name}
									</button>
								))}
							</div>
						</div>

						{/* Node List */}
						<div className="flex-1 overflow-y-auto">
							{groupedContent.map(
								category =>
									category.nodes.length > 0 && (
										<div key={category.id} className="p-4">
											<h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
												<div
													className={`w-2 h-2 rounded-full bg-${category.color}-500`}
												/>
												{category.name}
											</h3>
											<div className="space-y-1">
												{category.nodes.map(node => (
													<button
														key={node.id}
														onClick={() => handleNodeSelect(node)}
														className={`w-full text-left p-3 rounded-md transition-colors ${
															selectedNode?.id === node.id
																? `bg-${category.color}-50 border-${category.color}-200 border`
																: 'hover:bg-gray-50 dark:hover:bg-gray-700'
														}`}
													>
														<div className="flex items-start gap-3">
															<span className="text-lg">{node.icon}</span>
															<div className="flex-1 min-w-0">
																<div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
																	{node.name}
																</div>
																<div className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
																	{node.description}
																</div>
															</div>
														</div>
													</button>
												))}
											</div>
										</div>
									)
							)}

							{filteredContent.length === 0 && (
								<div className="p-8 text-center text-gray-500 dark:text-gray-400">
									<Search className="w-12 h-12 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
									<p className="text-lg font-medium mb-2">No nodes found</p>
									<p className="text-sm">
										Try adjusting your search terms or category filter
									</p>
								</div>
							)}
						</div>
					</div>

					{/* Main Content */}
					<div 
						className="flex-1 flex flex-col"
						onClick={(e) => e.stopPropagation()}
					>
						{selectedNode ? (
							<HelpArticle node={selectedNode} onBack={handleBack} />
						) : (
							<div className="flex-1 flex items-center justify-center text-center p-8">
								<div>
									<div className="text-6xl mb-4">📚</div>
									<h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
										Select a node to view its documentation
									</h3>
									<p className="text-gray-600 dark:text-gray-300 max-w-md">
										Choose from the list on the left to learn about node
										configuration, inputs, outputs, and see usage examples.
									</p>
								</div>
							</div>
						)}
					</div>
				</div>
			</div>
		);
		
		return createPortal(popupContent, document.body);
	}

	// If embedded, render with compact styling
	if (embedded) {
		return (
			<div className="h-full flex pointer-events-auto">
				{/* Sidebar */}
				<div className="w-80 flex flex-col border-r border-gray-200 dark:border-gray-700">
					{/* Header with close button */}
					<div className="p-4 border-b border-gray-200 dark:border-gray-700">
						<div className="flex items-center justify-between mb-4">
							<h2 className="text-xl font-bold text-gray-900 dark:text-white">
								Help & Documentation
							</h2>
							<button
								onClick={onClose}
								className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
							>
								<X className="w-5 h-5" />
							</button>
						</div>
						
						{/* Search */}
						<div className="relative">
							<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 w-4 h-4" />
							<input
								type="text"
								placeholder="Search nodes..."
								value={searchTerm}
								onChange={e => setSearchTerm(e.target.value)}
								className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
							/>
						</div>
					</div>

					{/* Category Filter */}
					<div className="p-4 border-b border-gray-200 dark:border-gray-700">
						<div className="flex flex-wrap gap-2">
							<button
								onClick={() => setSelectedCategory(null)}
								className={`px-3 py-1 text-sm rounded-md transition-colors ${
									selectedCategory === null
										? 'bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900'
										: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
								}`}
							>
								All
							</button>
							{HELP_CATEGORIES.map(category => (
								<button
									key={category.id}
									onClick={() => setSelectedCategory(category.id)}
									className={`px-3 py-1 text-sm rounded-md transition-colors ${
										selectedCategory === category.id
											? `bg-${category.color}-600 dark:bg-${category.color}-500 text-white`
											: `bg-${category.color}-100 dark:bg-${category.color}-900 text-${category.color}-700 dark:text-${category.color}-300 hover:bg-${category.color}-200 dark:hover:bg-${category.color}-800`
									}`}
								>
									{category.name}
								</button>
							))}
						</div>
					</div>

					{/* Node List */}
					<div className="flex-1 overflow-y-auto">
						{groupedContent.map(
							category =>
								category.nodes.length > 0 && (
									<div key={category.id} className="p-4">
										<h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
											<div
												className={`w-2 h-2 rounded-full bg-${category.color}-500`}
											/>
											{category.name}
										</h3>
										<div className="space-y-1">
											{category.nodes.map(node => (
												<button
													key={node.id}
													onClick={() => handleNodeSelect(node)}
													className={`w-full text-left p-3 rounded-md transition-colors ${
														selectedNode?.id === node.id
															? `bg-${category.color}-50 border-${category.color}-200 border`
															: 'hover:bg-gray-50'
													}`}
												>
													<div className="flex items-start gap-3">
														<span className="text-lg">{node.icon}</span>
														<div className="flex-1 min-w-0">
															<div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
																{node.name}
															</div>
															<div className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
																{node.description}
															</div>
														</div>
													</div>
												</button>
											))}
										</div>
									</div>
								)
						)}

						{filteredContent.length === 0 && (
							<div className="p-8 text-center text-gray-500 dark:text-gray-400">
								<Search className="w-12 h-12 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
								<p className="text-lg font-medium mb-2">No nodes found</p>
								<p className="text-sm">
									Try adjusting your search terms or category filter
								</p>
							</div>
						)}
					</div>
				</div>

				{/* Main Content */}
				<div className="flex-1 flex flex-col">
					{selectedNode ? (
						<HelpArticle node={selectedNode} onBack={handleBack} embedded={true} />
					) : (
						<div className="flex-1 flex items-center justify-center text-center p-8">
							<div>
								<div className="text-6xl mb-4">📚</div>
								<h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
									Select a node to view its documentation
								</h3>
								<p className="text-gray-600 dark:text-gray-300 max-w-md">
									Choose from the list on the left to learn about node
									configuration, inputs, outputs, and see usage examples.
								</p>
							</div>
						</div>
					)}
				</div>
			</div>
		);
	}

	// Full overlay mode (original implementation)
	return (
		<div className="fixed inset-0 z-[9999] flex bg-black/50 pointer-events-auto">
			{/* Overlay */}
			<div className="absolute inset-0" onClick={onClose} />

			{/* Help Menu */}
			<div className="relative flex w-full max-w-6xl mx-auto my-8 bg-white dark:bg-gray-800 rounded-lg shadow-xl pointer-events-auto">
				{/* Sidebar */}
				<div className="w-80 flex flex-col border-r border-gray-200 dark:border-gray-700">
					{/* Header */}
					<div className="p-4 border-b border-gray-200 dark:border-gray-700">
						<div className="flex items-center justify-between mb-4">
							<h2 className="text-xl font-bold text-gray-900 dark:text-white">
								Help & Documentation
							</h2>
							<button
								onClick={onClose}
								className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
							>
								<X className="w-5 h-5" />
							</button>
						</div>

						{/* Search */}
						<div className="relative">
							<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 w-4 h-4" />
							<input
								type="text"
								placeholder="Search nodes..."
								value={searchTerm}
								onChange={e => setSearchTerm(e.target.value)}
								className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
							/>
						</div>
					</div>

					{/* Category Filter */}
					<div className="p-4 border-b border-gray-200 dark:border-gray-700">
						<div className="flex flex-wrap gap-2">
							<button
								onClick={() => setSelectedCategory(null)}
								className={`px-3 py-1 text-sm rounded-md transition-colors ${
									selectedCategory === null
										? 'bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900'
										: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
								}`}
							>
								All
							</button>
							{HELP_CATEGORIES.map(category => (
								<button
									key={category.id}
									onClick={() => setSelectedCategory(category.id)}
									className={`px-3 py-1 text-sm rounded-md transition-colors ${
										selectedCategory === category.id
											? `bg-${category.color}-600 dark:bg-${category.color}-500 text-white`
											: `bg-${category.color}-100 dark:bg-${category.color}-900 text-${category.color}-700 dark:text-${category.color}-300 hover:bg-${category.color}-200 dark:hover:bg-${category.color}-800`
									}`}
								>
									{category.name}
								</button>
							))}
						</div>
					</div>

					{/* Node List */}
					<div className="flex-1 overflow-y-auto">
						{groupedContent.map(
							category =>
								category.nodes.length > 0 && (
									<div key={category.id} className="p-4">
										<h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
											<div
												className={`w-2 h-2 rounded-full bg-${category.color}-500`}
											/>
											{category.name}
										</h3>
										<div className="space-y-1">
											{category.nodes.map(node => (
												<button
													key={node.id}
													onClick={() => handleNodeSelect(node)}
													className={`w-full text-left p-3 rounded-md transition-colors ${
														selectedNode?.id === node.id
															? `bg-${category.color}-50 border-${category.color}-200 border`
															: 'hover:bg-gray-50'
													}`}
												>
													<div className="flex items-start gap-3">
														<span className="text-lg">{node.icon}</span>
														<div className="flex-1 min-w-0">
															<div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
																{node.name}
															</div>
															<div className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
																{node.description}
															</div>
														</div>
													</div>
												</button>
											))}
										</div>
									</div>
								)
						)}

						{filteredContent.length === 0 && (
							<div className="p-8 text-center text-gray-500 dark:text-gray-400">
								<Search className="w-12 h-12 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
								<p className="text-lg font-medium mb-2">No nodes found</p>
								<p className="text-sm">
									Try adjusting your search terms or category filter
								</p>
							</div>
						)}
					</div>
				</div>

				{/* Main Content */}
				<div className="flex-1 flex flex-col">
					{selectedNode ? (
						<HelpArticle node={selectedNode} onBack={handleBack} />
					) : (
						<div className="flex-1 flex items-center justify-center text-center p-8">
							<div>
								<div className="text-6xl mb-4">📚</div>
								<h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
									Select a node to view its documentation
								</h3>
								<p className="text-gray-600 dark:text-gray-300 max-w-md">
									Choose from the list on the left to learn about node
									configuration, inputs, outputs, and see usage examples.
								</p>
							</div>
						</div>
					)}
				</div>
			</div>
		</div>
	);
}
